import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, fmtNum, timeAgo } from "@/api";
import { X, ChartLine, ArrowUp, ArrowDown } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { TradingChart } from "@/components/TradingChart";

const LONG_CRITERIA = [
  "htf_bull_bias",
  "major_trend_flip",
  "weekly_structure_break",
  "ml_lorentzian",
  "ema_stack",
  "supertrend_up",
  "rsi_bounce",
  "unfilled_fvg",
  "volume_spike",
  "obv_rising",
  "bull_divergence",
  "double_bottom",
  "above_basis",
];
const SHORT_CRITERIA = [
  "htf_bear_bias",
  "major_trend_flip",
  "weekly_structure_break",
  "ml_lorentzian",
  "ema_stack",
  "supertrend_down",
  "rsi_rejection",
  "unfilled_fvg",
  "volume_spike",
  "obv_falling",
  "bear_divergence",
  "double_top",
  "below_basis",
];

export function PairDetail({ symbol, direction = "long", mode = "scalp", onClose }) {
  const isShort = direction === "short";
  const accent = isShort ? "#FF3B30" : "#00FF66";
  const critOrder = isShort ? SHORT_CRITERIA : LONG_CRITERIA;

  // Default chart TF picked from mode (scalp 5m, swing 1h, position 4h, macro 1D).
  const MODE_DEFAULT_TF = { scalp: "5m", swing: "1h", position: "4h", macro: "1D" };
  const [chartTf, setChartTf] = useState(MODE_DEFAULT_TF[mode] || "5m");

  const analysis = useQuery({
    queryKey: ["analysis", symbol, direction, mode],
    queryFn: () => api.analysis(symbol, MODE_DEFAULT_TF[mode] || "5m", direction, mode),
    enabled: !!symbol,
    refetchInterval: 30_000,
  });
  const klines = useQuery({
    queryKey: ["klines", symbol, chartTf],
    queryFn: () => api.klines(symbol, chartTf, 200),
    enabled: !!symbol,
    refetchInterval: 30_000,
  });
  const fvg = useQuery({
    queryKey: ["fvg-detail", symbol],
    queryFn: () => api.fvg(symbol),
    enabled: !!symbol,
    refetchInterval: 30_000,
  });

  const a = analysis.data;
  const candles = klines.data?.candles || [];
  const fvg5 = isShort
    ? (fvg.data?.fvg_5m_bear || []).filter((g) => !g.filled)
    : (fvg.data?.fvg_5m || []).filter((g) => !g.filled);
  const fvg1 = isShort
    ? (fvg.data?.fvg_1m_bear || []).filter((g) => !g.filled)
    : (fvg.data?.fvg_1m || []).filter((g) => !g.filled);

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex" data-testid="pair-detail-modal">
      <button
        className="flex-1 cursor-default"
        onClick={onClose}
        aria-label="close-backdrop"
        data-testid="pair-detail-backdrop"
      />
      <div className="w-full max-w-[880px] bg-[#0A0A0A] border-l border-white/10 h-full overflow-y-auto">
        <div className="sticky top-0 z-10 bg-[#0f0f0f] border-b border-white/10 px-5 py-3 flex items-center gap-3">
          <ChartLine size={18} style={{ color: accent }} />
          <div>
            <div className="flex items-center gap-2">
              <div className="font-display text-lg font-bold text-white tracking-wide">{symbol}</div>
              <span
                className="font-mono text-[10px] font-bold px-1.5 py-[1px] border tracking-widest uppercase flex items-center gap-0.5"
                style={{ color: accent, borderColor: `${accent}55`, background: `${accent}0f` }}
              >
                {isShort ? <ArrowDown size={10} weight="bold" /> : <ArrowUp size={10} weight="bold" />}
                {direction}
              </span>
            </div>
            <div className="font-mono text-[11px] text-white/50">
              {chartTf} · {mode.toUpperCase()} MODE · {a?.last_bar_time ? timeAgo(a.last_bar_time) : "loading…"}
            </div>
          </div>
          <Button
            data-testid="close-pair-detail"
            onClick={onClose}
            variant="outline"
            size="sm"
            className="ml-auto rounded-none border-white/15 bg-transparent hover:bg-white/5 text-white"
          >
            <X size={14} />
          </Button>
        </div>

        <div className="p-5 space-y-5">
          {analysis.isLoading && (
            <div className="font-mono text-sm text-white/40">Fetching analysis<span className="terminal-cycle">...</span></div>
          )}
          {a && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Metric label="Price" value={fmtNum(a.price)} />
                <Metric label="Score" value={a.score ?? "-"} color={a.score >= 60 ? accent : "#F5C518"} />
                <Metric
                  label="Lev Suggest"
                  value={a.recommended_leverage ? `${a.recommended_leverage}×` : "-"}
                  color="#F5C518"
                />
                <Metric
                  label="Eligible"
                  value={a.eligible ? direction.toUpperCase() : "WAIT"}
                  color={a.eligible ? accent : "#FF3B30"}
                />
                <Metric label="Stop Loss" value={a.stop_loss ? fmtNum(a.stop_loss) : "-"} color="#FF3B30" />
                <Metric label="TP1" value={a.take_profit_1 ? fmtNum(a.take_profit_1) : "-"} color={accent} />
                <Metric label="TP2" value={a.take_profit_2 ? fmtNum(a.take_profit_2) : "-"} color={accent} />
                <Metric label="RSI(14)" value={a.rsi ?? "-"} />
                <Metric
                  label="ADX(14)"
                  value={a.adx ?? "-"}
                  color={a.adx >= 25 ? "#00FF66" : a.adx >= 20 ? "#F5C518" : "#FF3B30"}
                />
                <Metric
                  label="Volatility"
                  value={a.volatility_regime ? `${a.volatility_regime.toUpperCase()} · ${a.regime_ratio}×` : "-"}
                  color={a.volatility_regime === "high" ? "#FF3B30" : a.volatility_regime === "low" ? "#00FF66" : "#F5C518"}
                />
              </div>

              <div>
                <div className="font-display text-sm tracking-widest uppercase text-white/70 mb-2">
                  Confluence Breakdown
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
                  {critOrder.map((k) => {
                    const c = a.criteria?.[k];
                    if (!c) return null;
                    return (
                      <div
                        key={k}
                        data-testid={`criteria-${k}`}
                        className={`border px-2 py-2 flex flex-col ${c.hit ? "" : "border-white/10 bg-[#0f0f0f] opacity-60"}`}
                        style={c.hit ? { borderColor: `${accent}55`, background: `${accent}0d` } : {}}
                      >
                        <span className="font-mono text-[9px] uppercase tracking-widest text-white/50">
                          W{c.weight}
                        </span>
                        <span
                          className="text-[13px] font-display font-semibold"
                          style={{ color: c.hit ? accent : "#ffffff40" }}
                        >
                          {c.label}
                        </span>
                        {c.value !== undefined && (
                          <span className="font-mono text-[10px] text-white/60 mt-0.5">= {c.value}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          <div>
            <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
              <div className="font-display text-sm tracking-widest uppercase text-white/70">
                Candlestick · BLOFIN-style · Entry / SL / TP overlaid
              </div>
              <div className="flex border border-white/10" data-testid="chart-tf-picker">
                {["1m", "5m", "15m", "1h", "4h", "1D", "1W"].map((t) => (
                  <button
                    key={t}
                    data-testid={`chart-tf-${t}`}
                    onClick={() => setChartTf(t)}
                    className={`px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest ${
                      chartTf === t ? "bg-white text-black" : "text-white/60 hover:bg-white/5"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <TradingChart
              candles={candles}
              direction={direction}
              entry={a?.entry}
              stop_loss={a?.stop_loss}
              take_profit_1={a?.take_profit_1}
              take_profit_2={a?.take_profit_2}
              markers={a?.eligible && a?.last_bar_time ? [{
                time: a.last_bar_time,
                direction,
                score: a.score,
                text: `${direction.toUpperCase()} ${a.score}`,
              }] : []}
              height={380}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FvgList title={`1m ${isShort ? "Bear" : "Bull"} FVGs`} list={fvg1} accent={accent} />
            <FvgList title={`5m ${isShort ? "Bear" : "Bull"} FVGs`} list={fvg5} accent={accent} />
          </div>
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value, color = "#FFFFFF" }) {
  return (
    <div className="border border-white/10 bg-[#0f0f0f] px-3 py-2">
      <div className="text-white/40 font-mono text-[10px] uppercase tracking-widest">{label}</div>
      <div className="font-mono text-base font-bold" style={{ color }}>{value}</div>
    </div>
  );
}

function FvgList({ title, list, accent }) {
  return (
    <div className="border border-white/10">
      <div className="px-3 py-2 bg-[#0f0f0f] border-b border-white/10 font-mono text-[11px] uppercase tracking-widest text-white/70">
        {title} <span className="ml-2" style={{ color: accent }}>{list.length}</span>
      </div>
      <div className="max-h-40 overflow-auto">
        {list.length === 0 && (
          <div className="p-3 text-center font-mono text-[11px] text-white/40">
            None — market efficient.
          </div>
        )}
        {list.map((g, i) => (
          <div
            key={i}
            className="px-3 py-1.5 border-b border-white/5 flex items-center gap-2 font-mono text-[11px]"
          >
            <span className="text-white/70">{fmtNum(g.low)} → {fmtNum(g.high)}</span>
            <span className="ml-auto text-[#F5C518]">{g.size_pct.toFixed(2)}%</span>
            <span className="text-white/40 text-[10px]">{g.age_bars}b</span>
          </div>
        ))}
      </div>
    </div>
  );
}
